import { useAuth } from "@assetwatch/auth";
import {
  type LDContext,
  LDProvider,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const LaunchDarklyStatusContext = createContext<{ isReady: boolean }>({
  isReady: true,
});

function LDContextIdentifier({
  children,
  context,
  isReadyToIdentify,
}: {
  children: ReactNode;
  context: LDContext;
  isReadyToIdentify: boolean;
}) {
  const ldClient = useLDClient();
  const [isReady, setIsReady] = useState(false);
  const lastIdentifiedContextKeyRef = useRef<string | null>(null);
  const contextKey = JSON.stringify(context);

  useEffect(() => {
    if (!ldClient) {
      setIsReady(false);
      return;
    }

    if (!isReadyToIdentify) {
      setIsReady(false);
      lastIdentifiedContextKeyRef.current = null;
      return;
    }

    if (lastIdentifiedContextKeyRef.current === contextKey) {
      setIsReady(true);
      return;
    }

    let isCancelled = false;

    setIsReady(false);
    lastIdentifiedContextKeyRef.current = contextKey;

    Promise.resolve(ldClient.identify(context))
      .catch(() => {
        lastIdentifiedContextKeyRef.current = null;
      })
      .finally(() => {
        if (!isCancelled) {
          setIsReady(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [context, contextKey, isReadyToIdentify, ldClient]);

  return (
    <LaunchDarklyStatusContext.Provider value={{ isReady }}>
      {children}
    </LaunchDarklyStatusContext.Provider>
  );
}

export function useLaunchDarklyStatus() {
  return useContext(LaunchDarklyStatusContext);
}

export default function LaunchDarklyProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

  if (!clientSideID) {
    return (
      <LaunchDarklyStatusContext.Provider value={{ isReady: true }}>
        {children}
      </LaunchDarklyStatusContext.Provider>
    );
  }

  const isUserContextReady =
    !isLoading && isAuthenticated && Boolean(user?.sub) && Boolean(user?.email);
  const userContext: LDContext = {
    kind: "user",
    key: user?.sub ?? "anonymous",
    email: user?.email ?? "",
    role: user?.role ?? "",
    roles: user?.roles ?? [],
    cognitoGroups: user?.cognitoGroups ?? [],
  };
  const anonymousContext: LDContext = {
    kind: "user",
    key: "anonymous",
    anonymous: true,
  };

  return (
    <LDProvider clientSideID={clientSideID} context={anonymousContext}>
      <LDContextIdentifier
        context={userContext}
        isReadyToIdentify={isUserContextReady}
      >
        {children}
      </LDContextIdentifier>
    </LDProvider>
  );
}
